/**
 * Runtime application + persistence for themes.
 *
 * A ThemeOption is anything the picker can select: a built-in Edge theme, a
 * shipped starter preset, or a user's custom theme. Built-ins apply via their
 * CSS class only (the hand-tuned palette lives in index.css); derived options
 * apply the base-mode class PLUS inline CSS-var overrides for every semantic
 * token.
 */
import {
  type BaseRoles,
  type CustomTheme,
  CustomThemeSchema,
  DERIVED_TOKEN_KEYS,
  deriveTokens,
} from './theme-model'

export interface ThemeOption {
  id: string
  name: string
  /** which CSS class / invariant-palette base this theme layers on */
  baseMode: 'light' | 'dark' | 'black'
  /** 8 base roles — also used for the picker's swatch preview */
  roles: BaseRoles
  /** Advanced per-token overrides (token-name → HSL triplet) */
  overrides?: Record<string, string>
  /** true → apply via class only, no inline tokens (the static Edge themes) */
  builtin?: boolean
}

const ACTIVE_KEY = 'edge_theme' // repurposed: now holds a theme id (back-compat with 'light'|'dark'|'black')
const CUSTOM_KEY = 'edge_custom_themes'
const CUSTOM_CAP = 24 // bound the custom-theme store per the localStorage-quota house rule

const THEME_CLASSES = ['light', 'dark', 'black']

/**
 * Full token map for a theme option, or `null` for built-ins (which rely
 * entirely on their index.css class and get no inline overrides).
 */
export function resolveThemeTokens(opt: ThemeOption): Record<string, string> | null {
  if (opt.builtin) return null
  return { ...deriveTokens(opt.roles), ...(opt.overrides ?? {}) }
}

/**
 * Apply a theme to a root element: swap the mode class and (for derived themes)
 * write inline CSS-var overrides. Always clears prior inline token overrides
 * first so switching themes never leaves stale vars behind.
 */
export function applyThemeOption(opt: ThemeOption, root: HTMLElement): void {
  root.classList.remove(...THEME_CLASSES)
  root.classList.add(opt.baseMode)
  // Theme identity hook for CSS that targets ONE theme rather than a base
  // mode — e.g. the mosaic panel gradient applies only to Edge Dark
  // (html[data-theme-id='dark']); every other theme gets a flat surface.
  root.dataset.themeId = opt.id
  // Mirror the base mode for index.html's pre-paint stamp, so a preset/custom
  // theme's LIGHT vs DARK base is known before React boots (prevents the
  // wrong-mode flash on first paint).
  try {
    localStorage.setItem('edge_theme_mode', opt.baseMode)
  } catch {
    // ignore quota / privacy-mode failures
  }

  const style = root.style
  for (const key of DERIVED_TOKEN_KEYS) style.removeProperty(`--${key}`)

  const tokens = resolveThemeTokens(opt)
  if (tokens) {
    for (const [key, value] of Object.entries(tokens)) {
      style.setProperty(`--${key}`, value)
    }
  }
}

// ── Persistence ──────────────────────────────────────────────────────────────

export function loadActiveThemeId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_KEY)
  } catch {
    return null
  }
}

export function saveActiveThemeId(id: string): void {
  try {
    localStorage.setItem(ACTIVE_KEY, id)
  } catch {
    // ignore quota / privacy-mode failures
  }
}

export function loadCustomThemes(): CustomTheme[] {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // drop any entries that don't validate rather than throwing the whole set away
    return parsed
      .map((entry) => CustomThemeSchema.safeParse(entry))
      .filter((r): r is { success: true; data: CustomTheme } => r.success)
      .map((r) => r.data)
  } catch {
    return []
  }
}

export function saveCustomThemes(themes: CustomTheme[]): void {
  try {
    // keep the most-recent CUSTOM_CAP (newest assumed at the end)
    const bounded = themes.slice(-CUSTOM_CAP)
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(bounded))
  } catch {
    // ignore quota / privacy-mode failures
  }
}

/** Upsert a custom theme into a list by id (newest moves to the end). */
export function upsertCustomTheme(list: CustomTheme[], theme: CustomTheme): CustomTheme[] {
  const without = list.filter((t) => t.id !== theme.id)
  return [...without, theme]
}

/** A CustomTheme rendered as a selectable ThemeOption. */
export function customThemeToOption(theme: CustomTheme): ThemeOption {
  return {
    id: theme.id,
    name: theme.name,
    baseMode: theme.baseMode,
    roles: theme.roles,
    overrides: theme.overrides,
    builtin: false,
  }
}
